import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import bonafideService from "../../services/bonafideService";
import BonafideCertificate from "../../components/BonafideCertificate";
import PrintInstructions from "../../components/common/PrintInstructions";

const PRINT_MODES = [
  {
    value: "printed",
    label: "🖨 Printed Format",
    desc: "Values only — load pre-printed Bonafide form in printer tray. Sr. No. is skipped (already on form).",
  },
  {
    value: "plain",
    label: "📄 Plain Paper",
    desc: "Full certificate with header, labels, borders and Sr. No. on blank paper (7.40 × 6.73 in).",
  },
];

export default function BonafidePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isClerk } = useApp();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState("printed");
  const [previewMode, setPreviewMode] = useState(false);

  const listPath = isClerk ? "/clerk/bonafide" : "/bonafide";
  const editPath = isClerk ? `/clerk/bonafide/${id}/edit` : `/bonafide/${id}/edit`;

  useEffect(() => {
    (async () => {
      try {
        const res = await bonafideService.getById(id);
        setRecord(res.data);
      } catch (err) {
        toast.error(err.message);
        navigate(listPath);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, listPath]);

  const handleModeChange = (mode) => {
    setPrintMode(mode);
    if (mode === "plain") setPreviewMode(false);
  };

  const handlePrint = () => {
    setPreviewMode(false);
    setTimeout(() => window.print(), 150);
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading certificate...
      </div>
    );
  }

  if (!record) return null;

  const printData = {
    srNo: record.srNo,
    certificateDate: record.certificateDate,
    studentName: record.student?.fullName,
    className: record.className,
    sessionStart: record.sessionStart,
    sessionEnd: record.sessionEnd,
    dob: record.student?.dob,
    dobInWords: record.student?.dobInWords,
  };

  const modeInfo = PRINT_MODES.find((m) => m.value === printMode);

  return (
    <div>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div
        className="no-print"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <button className="btn btn-outline" onClick={() => navigate(listPath)}>
          ← Back
        </button>

        <div style={{ flex: 1 }}>
          <h1 className="page-title">
            Bonafide Certificate — {record.student?.fullName}
          </h1>
          <p className="page-subtitle">
            Sr. No. {record.srNo} · Session {record.sessionStart}–{record.sessionEnd} ·{" "}
            {record.certificateDate}
          </p>
        </div>

        {/* Print Mode */}
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <label
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Print Mode
          </label>
          <select
            value={printMode}
            onChange={(e) => handleModeChange(e.target.value)}
            className="form-control"
            style={{ width: "200px", fontSize: 13, fontWeight: 500 }}
          >
            {PRINT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Calibration toggle — only for Printed Format */}
        {printMode === "printed" && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              fontSize: 13,
              color: "var(--text-muted)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={previewMode}
              onChange={(e) => setPreviewMode(e.target.checked)}
            />
            Show template (calibration)
          </label>
        )}

        <button className="btn btn-outline" onClick={() => navigate(editPath)}>
          ✏️ Edit
        </button>
        <button className="btn btn-success" onClick={handlePrint}>
          🖨️ Print Certificate
        </button>
      </div>

      {/* ── Print instructions banner ─────────────────────────────────── */}
      <PrintInstructions />

      {/* ── Mode banner ──────────────────────────────────────────────────── */}
      <div
        className="no-print"
        style={{
          background: printMode === "plain" ? "#f0f9ff" : "#f0fdf4",
          border: `1px solid ${printMode === "plain" ? "#7dd3fc" : "#86efac"}`,
          borderRadius: 8,
          padding: "0.6rem 1rem",
          marginBottom: "1rem",
          fontSize: 13,
          color: printMode === "plain" ? "#0c4a6e" : "#14532d",
        }}
      >
        <strong>{modeInfo?.label}</strong> — {modeInfo?.desc}
        {printMode === "printed" && previewMode && (
          <span style={{ marginLeft: "1rem", color: "#854d0e", fontWeight: 600 }}>
            ⚠️ Calibration ON — turn OFF before printing
          </span>
        )}
      </div>

      {/* ── Certificate ──────────────────────────────────────────────────── */}
      <BonafideCertificate
        data={printData}
        printMode={printMode}
        previewMode={previewMode}
        college={record.college}
        templateConfig={record.college?.bonafideTemplate}
      />
    </div>
  );
}